import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Truck, CheckCircle2, Package, Clock, XCircle, Loader2 } from "lucide-react";

const STAGES = ["unfulfilled", "processing", "shipped", "delivered"];
const LABELS = { unfulfilled: "Order received", processing: "Preparing", shipped: "Shipped", delivered: "Delivered" };
const STAGE_ICONS = [CheckCircle2, Clock, Truck, Package];

export default function TrackByToken() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/orders/track/${encodeURIComponent(token)}`);
        if (alive) {
          setOrder(r.data);
          setStatus("ok");
        }
      } catch {
        if (alive) setStatus("notfound");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const stageIdx = order ? Math.max(0, STAGES.indexOf(order.fulfillment_status)) : -1;

  return (
    <div className="fade-in" data-testid="page-track-token">
      <SEO title="Track your order" path={`/track/${token}`} />
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <p className="eyebrow mb-4">Order tracking</p>
        <h1 className="text-h1 text-[#1D1D1F]">Your order</h1>

        {status === "loading" && (
          <div className="mt-10 flex items-center gap-3 text-[#86868B]" data-testid="track-loading">
            <Loader2 size={20} className="animate-spin" /> Loading your order…
          </div>
        )}

        {status === "notfound" && (
          <div className="mt-10 soft-card p-8 flex items-start gap-4" data-testid="track-notfound">
            <XCircle size={22} className="text-[#FF3B30] mt-1" />
            <div>
              <p className="font-semibold">This tracking link is invalid or has expired.</p>
              <p className="text-sm text-[#86868B] mt-2">
                Double-check the link from your order confirmation email. If it still doesn’t work, use the manual lookup below or contact support.
              </p>
              <div className="mt-4 flex gap-3">
                <Link to="/track" className="btn-secondary">Look up with order number</Link>
                <Link to="/contact" className="btn-primary">Contact support</Link>
              </div>
            </div>
          </div>
        )}

        {status === "ok" && order && (
          <div className="mt-12 space-y-8" data-testid="track-result">
            <div className="soft-card p-6 md:p-8">
              <p className="text-xs text-[#86868B]">Order</p>
              <p className="font-display text-2xl font-bold">#{order.order_number}</p>
              <p className="text-sm text-[#86868B] mt-1">Placed {new Date(order.created_at).toLocaleDateString()}</p>

              <div className="mt-8 grid grid-cols-4 gap-2">
                {STAGES.map((s, i) => {
                  const active = i <= stageIdx;
                  const Icon = STAGE_ICONS[i];
                  return (
                    <div key={s} className="text-center">
                      <div className={`w-10 h-10 mx-auto rounded-full grid place-items-center ${active ? "bg-[#0066FF] text-white" : "bg-[#E5E5EA] text-[#86868B]"}`}>
                        <Icon size={16} />
                      </div>
                      <p className={`mt-2 text-xs ${active ? "text-[#1D1D1F] font-semibold" : "text-[#86868B]"}`}>{LABELS[s]}</p>
                    </div>
                  );
                })}
              </div>

              {order.tracking_number && (
                <div className="mt-8 p-4 bg-white rounded-2xl border border-black/5">
                  <p className="text-xs text-[#86868B]">Tracking</p>
                  <p className="font-mono-tech text-sm">{order.shipping_carrier} · {order.tracking_number}</p>
                </div>
              )}
            </div>

            <div className="soft-card p-6 md:p-8">
              <p className="eyebrow mb-4">Items</p>
              <div className="space-y-4">
                {order.lines.map((l) => (
                  <div key={`${l.product_name}-${l.variant_name}`} className="flex gap-3 items-center">
                    {l.image && <img src={l.image} alt="" className="w-16 h-16 rounded-xl object-cover bg-white" />}
                    <div className="flex-1">
                      <p className="font-medium">{l.product_name}</p>
                      <p className="text-xs text-[#86868B]">{l.variant_name} × {l.quantity}</p>
                    </div>
                    <p className="text-sm font-semibold">${l.line_total.toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-right text-sm">Total <span className="font-display text-lg font-bold ml-2">${order.total.toFixed(2)}</span></p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
